// Vue
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/index";
import { createHead } from "@unhead/vue";
import { initStaticHeadDedupe } from "@/scripts/staticHeadDedupe.js";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faCheckCircle,
  faLock,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faCheck,
  faCalendarCheck,
  faGraduationCap,
  faComments,
  faInfoCircle,
  faUser,
  faBook,
  faMessage,
  faSignOutAlt,
  faBars,
  faChevronRight,
  faChevronLeft,
  faMicrophone,
  faEllipsis,
  faXmark,
  faArrowUp,
  faPlay,
  faPause,
  faSun,
  faMoon,
  faCircleHalfStroke,
  faHourglassEnd,
  faPlus,
  faTriangleExclamation,
  faTrashCan,
  faGift,
  faStopwatch,
  faTrophy,
  faArrowUpFromBracket,
  faDownload,
  faLink,
} from "@fortawesome/free-solid-svg-icons";

// Project utilities (replaces Bootstrap)
import "@/assets/utilities.css";

// Stamp the runtime platform on <html> so CSS can make per-platform layout
// decisions (e.g. --al-tab-bar-inset in utilities.css: the bottom tab bar
// reserves the gesture-bar inset on Android but hugs the screen edge on iOS).
// "web" covers mobile and desktop browsers.
document.documentElement.classList.add(
  `platform-${window.Capacitor?.getPlatform?.() || "web"}`
);

// Side-effect: instantiates the shared playback service. On iOS this routes
// through a native AVAudioPlayer plugin; elsewhere it primes a shared HTML5
// <audio> element on the first user gesture.
import "@/scripts/audioPlaybackService";

// Google Analytics
import VueGtag from "vue-gtag";

// i18n for localization
import i18n from "./locales";
import { initAuthI18n } from "./store/auth";

// Theme (dark mode) — initialized before mount so useTheme owns the
// data-theme attribute stamped by the pre-paint snippet in index.html
import { initTheme } from "@/composables/useTheme";

// Runtime feature flags — subscribe to RTDB once at startup so
// featureFlags.<name> reflects live admin toggles (no redeploy needed).
import { initFeatureFlags } from "@/store/featureFlags.js";

// The speed chip's preference: restores the device cache, follows the
// audioPlaybackSpeed flag and mirrors the learner's choice to their account.
import { initPlaybackRateSync } from "@/scripts/playbackRateSync.js";

// The phrase replay clip cache: follows the phraseReplay flag, fetches clips
// the way the pill does, and empties when the signed-in account goes away.
import { initPhraseReplaySync } from "@/scripts/phraseReplaySync.js";

// Firebase
import { initializeApp } from "firebase/app";
import { initializeAuth, browserLocalPersistence } from "firebase/auth";
import firebaseConfig from "./firebase-config.js";
import { wireEmulators } from "@/scripts/emulatorWiring.js";

// Lead-source capture. Runs before the router is installed below, because the
// router's first navigation (and the first router.push after sign-in) replaces
// the URL and discards the landing page's utm_* query string.
import { startAcquisitionCapture } from "@/scripts/acquisition.js";
startAcquisitionCapture();

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

// Inside Capacitor WebViews, Firebase Auth's IndexedDB persistence layer can
// hang and the popup/redirect iframe (apis.google.com gapi) throws CORS
// errors against the capacitor:// origin. Use initializeAuth (instead of
// getAuth) to opt into localStorage persistence (which works fine in
// capacitor:// WebViews) and skip the popup/redirect resolver — we sign in
// via the native plugin + signInWithCredential, never via the web
// popup/redirect flow. localStorage persistence means getAuth().currentUser
// is restored on cold launch, so RTDB / Firestore reads stay authenticated.
if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.() === true) {
  initializeAuth(firebaseApp, { persistence: browserLocalPersistence });
}
// On a local dev server, point the database SDK at the database emulator, and
// the Auth SDK at the auth emulator when VUE_APP_AUTH_EMULATOR_PORT opts in.
// The auth half never creates Auth itself; see src/scripts/emulatorWiring.js
// for why that matters.
wireEmulators(firebaseApp);

// Initialize HTML Head
const head = createHead();

// Pages served from dist/ arrive with a crawler-facing head already written
// into them at build time (src/scripts/injectHeadTags.js), so that a link
// preview exists before any script runs. This reconciles that head with the one
// the views write through useHead, so the rendered document ends up with
// exactly one tag per key. Registered here, before anything mounts, because it
// hooks unhead's render and must be listening for the very first one.
initStaticHeadDedupe({ head });

// Add the icons to the library
library.add(
  faCheckCircle,
  faLock,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faCheck,
  faCalendarCheck,
  faGraduationCap,
  faComments,
  faInfoCircle,
  faUser,
  faBook,
  faMessage,
  faSignOutAlt,
  faBars,
  faChevronRight,
  faChevronLeft,
  faMicrophone,
  faEllipsis,
  faXmark,
  faArrowUp,
  faPlay,
  faPause,
  faSun,
  faMoon,
  faCircleHalfStroke,
  faHourglassEnd,
  faPlus,
  faTriangleExclamation,
  faTrashCan,
  faGift,
  faStopwatch,
  faTrophy,
  faArrowUpFromBracket,
  faDownload,
  faLink
);

// Directives
import { ripple } from "@/directives/ripple.js";

// Initialize Vue
const vue = createApp(App)
  .use(router)
  .use(head)
  .use(i18n)
  .use(VueGtag, {
    config: { id: "G-ZY847CPJ46" },
  })
  .directive("ripple", ripple)
  .component("font-awesome-icon", FontAwesomeIcon);

// Initialize auth store with i18n instance for locale switching
initAuthI18n(i18n);

initTheme();
initFeatureFlags();
initPlaybackRateSync();
initPhraseReplaySync();

vue.mount("#app");
console.debug(`==== ${process.env.NODE_ENV} Restarted ====`);
